import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Stack,
  Divider,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  SentimentVerySatisfied,
  Recommend,
  WorkspacePremium,
  TrendingUp,
  Download,
  Refresh,
  Home,
  FilterAlt,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

const PRIMARY = '#1a237e';
const SUCCESS = '#2e7d32';
const WARNING = '#ed6c02';
const ERROR = '#d32f2f';
const LIGHT_GREEN = '#81c784';

const VOCDashboard = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('Overall Score');

  return (
    <Box sx={{ p: 3, backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: PRIMARY }}>
            Voice of Customer - {activeTab}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{
              bgcolor: 'white',
              '&:hover': { bgcolor: alpha(ERROR, 0.08) },
            }}
          >
            <Download sx={{ color: ERROR, fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              bgcolor: 'white',
              '&:hover': { bgcolor: alpha(PRIMARY, 0.08) },
            }}
          >
            <Refresh sx={{ fontSize: 20 }} />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              bgcolor: 'white',
              '&:hover': { bgcolor: alpha(PRIMARY, 0.08) },
            }}
          >
            <Home sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Filter Buttons */}
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mb: 3,
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha(PRIMARY, 0.2), borderRadius: 2 },
        }}
      >
        {['Feeling Safe', 'Feeling Supported', 'Feeling Connected', 'Feeling in Control', 'Branch Overview', 'Comments Details'].map(
          (tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'contained' : 'outlined'}
              onClick={() => setActiveTab(tab)}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                minWidth: 150,
                flexShrink: 0,
                ...(activeTab === tab
                  ? {
                      bgcolor: PRIMARY,
                      '&:hover': { bgcolor: alpha(PRIMARY, 0.8) },
                    }
                  : {
                      borderColor: alpha(PRIMARY, 0.2),
                      color: 'text.primary',
                      '&:hover': { borderColor: PRIMARY, bgcolor: alpha(PRIMARY, 0.04) },
                    }),
              }}
            >
              {tab}
            </Button>
          )
        )}
      </Stack>

      {/* Section 1: Pie Charts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
          mb: 4,
        }}
      >
        {/* Overall Satisfaction */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} textAlign="center" mb={2}>
            Overall Satisfaction (Last 4 Months)
          </Typography>
          <Box sx={{ height: 220, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={satisfactionData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {satisfactionData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 20,
                fontWeight: 700,
                color: PRIMARY,
              }}
            >
              850
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 1,
              flexWrap: 'wrap',
            }}
          >
            {satisfactionData.map((d) => (
              <Chip
                key={d.name}
                label={`${d.name} ${d.percent}%`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem' }}
              />
            ))}
          </Box>
        </Paper>

        {/* NPS / Recommendation Scale */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} textAlign="center" mb={2}>
            Likelihood to Recommend (Scale 1-10)
          </Typography>
          <Box sx={{ height: 220, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={npsData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {npsData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 20,
                fontWeight: 700,
                color: PRIMARY,
              }}
            >
              850
            </Typography>
          </Box>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            textAlign="center"
            mt={1}
          >
            0 = Declined to comment
          </Typography>
        </Paper>

        {/* Commendations Above and Beyond */}
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} textAlign="center" mb={2}>
            Commendations: Above and Beyond
          </Typography>
          <Box sx={{ height: 220, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={commendationData}
                  innerRadius={60}
                  outerRadius={80}
                  dataKey="value"
                >
                  {commendationData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 20,
                fontWeight: 700,
                color: PRIMARY,
              }}
            >
              850
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 3,
              mt: 1,
            }}
          >
            <Typography variant="body2" color={SUCCESS} fontWeight={600}>
              Yes: 178
            </Typography>
            <Typography variant="body2" color={WARNING} fontWeight={600}>
              No: 671
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* Section 2: Trend Analysis */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '8fr 4fr' },
          gap: 3,
          mb: 4,
        }}
      >
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Satisfaction Trend by Month
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis hide />
                <RechartsTooltip />
                <Bar dataKey="verySatisfied" stackId="a" fill={SUCCESS} />
                <Bar dataKey="satisfied" stackId="a" fill={LIGHT_GREEN} />
                <Bar dataKey="neutral" stackId="a" fill={WARNING} />
                <Bar dataKey="dissatisfied" stackId="a" fill={ERROR} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={600} mb={3}>
            Commendation Growth
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trendData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <RechartsTooltip />
                <Bar
                  dataKey="commendations"
                  fill={SUCCESS}
                  radius={[4, 4, 0, 0]}
                />
                <Bar dataKey="denials" fill={WARNING} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Box>

      {/* Section 3: Raw Feedback Table */}
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, overflow: 'hidden' }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6" fontWeight={600}>
            Recent Service User Feedback
          </Typography>
        </Box>
        <Table size="small">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Service User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date Submitted</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Satisfaction Level</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Recommendation (1-10)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Above & Beyond</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbackTableData.map((row) => (
              <TableRow key={row.user} hover>
                <TableCell>
                  <Typography fontWeight={600}>{row.user}</Typography>
                </TableCell>
                <TableCell>{row.date}</TableCell>
                <TableCell>
                  <Chip
                    label={row.satisfaction}
                    size="small"
                    sx={{
                      bgcolor:
                        row.satisfaction === 'Very satisfied'
                          ? SUCCESS
                          : LIGHT_GREEN,
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} textAlign="center">
                    {row.nps}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={row.commendation}
                    size="small"
                    color={row.commendation === 'Yes' ? 'success' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Mock Data
const satisfactionData = [
  { name: 'Very satisfied', value: 26.5, color: SUCCESS, percent: 26.5 },
  { name: 'Satisfied', value: 68.7, color: LIGHT_GREEN, percent: 68.7 },
  { name: 'Neutral', value: 2.5, color: WARNING, percent: 2.5 },
  { name: 'Dissatisfied', value: 2.3, color: ERROR, percent: 2.3 },
];

const npsData = [
  { name: '10', value: 24.8, color: '#1b5e20' },
  { name: '9', value: 42.5, color: SUCCESS },
  { name: '8', value: 10.8, color: LIGHT_GREEN },
  { name: 'Declined', value: 10.4, color: '#9e9e9e' },
];

const commendationData = [
  { name: 'Yes', value: 178, color: SUCCESS },
  { name: 'No', value: 671, color: WARNING },
];

const trendData = [
  {
    name: 'Oct-25',
    verySatisfied: 23,
    satisfied: 68,
    neutral: 5,
    dissatisfied: 4,
    commendations: 21,
    denials: 83,
  },
  {
    name: 'Nov-25',
    verySatisfied: 20,
    satisfied: 75,
    neutral: 3,
    dissatisfied: 2,
    commendations: 23,
    denials: 154,
  },
  {
    name: 'Dec-25',
    verySatisfied: 23,
    satisfied: 75,
    neutral: 2,
    dissatisfied: 0,
    commendations: 8,
    denials: 133,
  },
  {
    name: 'Jan-26',
    verySatisfied: 29,
    satisfied: 65,
    neutral: 4,
    dissatisfied: 2,
    commendations: 104,
    denials: 244,
  },
];

const feedbackTableData = [
  {
    user: 'Rachel Douglas',
    date: '05/02/26',
    satisfaction: 'Very satisfied',
    nps: 9,
    commendation: 'Yes',
  },
  {
    user: 'Robert Hall',
    date: '05/02/26',
    satisfaction: 'Satisfied',
    nps: 8,
    commendation: 'No',
  },
  {
    user: 'Paula Neil',
    date: '05/02/26',
    satisfaction: 'Very satisfied',
    nps: 9,
    commendation: 'Yes',
  },
];

export default VOCDashboard;